import { useEffect, useRef, useState } from 'react';

const CHARS = '!<>-_\\/[]{}—=+*^?#________';

type Props = {
  text: string;
  className?: string;
  duration?: number;
  trigger?: 'hover' | 'visible';
};

/**
 * Cyberpunk-style text scramble — letters rapidly cycle through random
 * symbols and resolve into the target string. Triggers on hover or when
 * scrolled into view.
 */
export function TextScramble({ text, className = '', duration = 900, trigger = 'hover' }: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const frame = useRef(0);
  const queue = useRef<{ from: string; to: string; start: number; end: number; char?: string }[]>([]);
  const rafRef = useRef<number>(0);
  const [display, setDisplay] = useState(text);

  const scramble = (newText: string, oldText = display) => {
    const length = Math.max(oldText.length, newText.length);
    queue.current = [];
    for (let i = 0; i < length; i++) {
      const from = oldText[i] || '';
      const to = newText[i] || '';
      const start = Math.floor(Math.random() * 40);
      const end = start + Math.floor(Math.random() * 40);
      queue.current.push({ from, to, start, end });
    }
    cancelAnimationFrame(rafRef.current);
    frame.current = 0;
    const update = () => {
      let output = '';
      let complete = 0;
      for (let i = 0, n = queue.current.length; i < n; i++) {
        let { from, to, start, end, char } = queue.current[i];
        if (frame.current >= end) {
          complete++;
          output += to;
        } else if (frame.current >= start) {
          if (!char || Math.random() < 0.28) {
            char = CHARS[Math.floor(Math.random() * CHARS.length)];
            queue.current[i].char = char;
          }
          output += char;
        } else {
          output += from;
        }
      }
      setDisplay(output);
      if (complete === queue.current.length) return;
      frame.current++;
      rafRef.current = requestAnimationFrame(update);
    };
    update();
  };

  useEffect(() => {
    if (trigger !== 'visible' || !ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            scramble(text);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, trigger]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  return (
    <span
      ref={ref}
      className={className}
      onMouseEnter={trigger === 'hover' ? () => scramble(text) : undefined}
    >
      {display}
    </span>
  );
}
